import axios from "axios";
import {
  LoginResponse,
  RoleType,
  SignupRequest,
  SignupResponse,
  TokenResponse,
  User,
} from "../../types/RoleType";
import type { PendingProfileSelection } from "../../types/auth";
import type { PhoneVerificationChallenge } from "../../types/RoleType";
import { decodeJwtToken } from "../../utils";
import { axiosInstance } from "./axios";

const API_URL = import.meta.env.VITE_API_URL;

function parseBranchIds(raw: unknown): number[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(Number);
  if (typeof raw === 'string') {
    try { return (JSON.parse(raw) as unknown[]).map(Number); } catch { /* fall through */ }
    return [Number(raw)].filter(n => !isNaN(n));
  }
  return [Number(raw)].filter(n => !isNaN(n));
}

/**
 * IMPORTANT: Password Security
 *
 * Passwords are sent as plain text over HTTPS (not Base64 encoded).
 *
 * - Base64 is encoding, NOT encryption. It provides zero security.
 * - HTTPS encrypts all data in transit, including passwords.
 * - The backend should hash passwords using bcrypt, argon2, or similar.
 *
 * REQUIREMENT: This application MUST be served over HTTPS in production.
 */

export type LoginResult =
  | ({ kind: "session" } & { user: User } & TokenResponse)
  | { kind: "profileSelection"; pendingProfileSelection: PendingProfileSelection }
  | { kind: "phoneVerification"; phoneVerification: PhoneVerificationChallenge }
  | { kind: "passwordSetup"; challengeToken: string; expiresAt: string }
  | { kind: "requiresPassword"; username: string };

export const AuthAPI = {
  login: async (
    username: string,
    password: string,
    userType: string
  ): Promise<LoginResult> => {
    try {
      const response = await axios.post<LoginResponse>(
        API_URL + "/auth/Login",
        {
          username: username,
          hashedPassword: password,
          userType: userType,
        },
        { timeout: 50000 }
      );

      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.[0] || "Login failed");
      }

      const data = response.data.data;

      // 2-step student login: student must enter password before proceeding
      if (data.requiresPassword) {
        return { kind: "requiresPassword", username: username };
      }

      // Password setup required (first-time student login)
      if (data.requiresPasswordSetup && data.passwordSetup) {
        return {
          kind: "passwordSetup",
          challengeToken: data.passwordSetup.challengeToken,
          expiresAt: data.passwordSetup.expiresAt,
        };
      }

      // Phone verification required (unverified student)
      if (data.requiresPhoneVerification && data.phoneVerification) {
        return {
          kind: "phoneVerification",
          phoneVerification: data.phoneVerification,
        };
      }

      // Multi-student path: backend signals profile selection required
      if (data.requiresProfileSelection && data.profileSelection) {
        return {
          kind: "profileSelection",
          pendingProfileSelection: {
            token: data.profileSelection.token,
            profiles: data.profileSelection.profiles,
            expiresAt: data.profileSelection.expiresAt,
          },
        };
      }

      const { token, refreshToken, permissions, userImageURL, refreshTokenExpiryTime, forcePasswordReset, forcePinChange, emailConfirmed } = data;
      const decodedToken = decodeJwtToken(token);
      if (!decodedToken) {
        throw new Error("Invalid token received");
      }
      return {
        kind: "session",
        user: {
          id: decodedToken.sub,
          email: decodedToken.email,
          username: decodedToken.username,
          firstName: decodedToken.firstName,
          lastName: decodedToken.lastName,
          roleType: decodedToken.userType as RoleType,
          roles: decodedToken.roles,
          permissions: permissions,
          profileImageUrl: userImageURL ?? undefined,
          instituteId: parseInt(decodedToken.InstituteId!, 10),
          branchIds: parseBranchIds(decodedToken.branchIds),
          personId: parseInt(decodedToken.id!, 10),
          lastLogin: decodedToken.lastLogin,
          uniqueId: decodedToken.uniqueId,
          instituteName: decodedToken.instituteName,
          isSubscribed: decodedToken.isSubscribed === "true",
        },
        accessToken: token,
        refreshToken: refreshToken,
        expiresAt: decodedToken.exp * 1000,
        refreshTokenExpiresAt: new Date(refreshTokenExpiryTime).getTime(),
        forcePasswordReset: forcePasswordReset,
        forcePinChange: forcePinChange,
        emailConfirmed: emailConfirmed,
      };
    } catch (error) {
      const axiosMsg =
        (error as { response?: { data?: { messages?: string[] } } })
          ?.response?.data?.messages?.[0];
      throw { message: axiosMsg || (error instanceof Error ? error.message : "An error occurred during login") };
    }
  },
  logout: async (userId: string, refreshToken: string) => {
    await axios.post(API_URL + "/auth/logout", {
      refreshToken: refreshToken,
      userId: userId,
    });
  },
  logoutAll: async (userId: string) => {
    await axios.post(API_URL + "/auth/logout-all", {
      userId: userId,
    });
  },
  refreshToken: async (
    token: string,
    refreshToken: string
  ): Promise<TokenResponse> => {
    const response = await axiosInstance.post("auth/refresh", {
      token: token,
      refreshToken: refreshToken,
    });

    const { newAccessToken, newRefreshToken, refreshTokenExpiryTime } =
      response.data.data;
    const decodedToken = decodeJwtToken(newAccessToken);

    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
      expiresAt: decodedToken.exp * 1000,
      refreshTokenExpiresAt: new Date(refreshTokenExpiryTime).getTime(),
    };
  },

  selectProfile: async (
    profileSelectionToken: string,
    studentId: string
  ): Promise<{ user: User } & TokenResponse> => {
    const response = await axiosInstance.post<LoginResponse>(
      "auth/SelectProfile",
      { profileSelectionToken, studentId }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || "Profile selection failed");
    }
    const { token, refreshToken, permissions, userImageURL, refreshTokenExpiryTime, forcePasswordReset, forcePinChange, emailConfirmed } =
      response.data.data;
    const decodedToken = decodeJwtToken(token);
    if (!decodedToken) throw new Error("Invalid token received");
    return {
      user: {
        id: decodedToken.sub,
        email: decodedToken.email,
        username: decodedToken.username,
        firstName: decodedToken.firstName,
        lastName: decodedToken.lastName,
        roleType: decodedToken.userType as RoleType,
        roles: decodedToken.roles,
        permissions,
        profileImageUrl: userImageURL ?? undefined,
        instituteId: parseInt(decodedToken.InstituteId!, 10),
        branchIds: decodedToken.branchIds
          ? (Array.isArray(decodedToken.branchIds) ? decodedToken.branchIds : [decodedToken.branchIds]).map(Number)
          : [],
        personId: parseInt(decodedToken.id!, 10),
        lastLogin: decodedToken.lastLogin,
        uniqueId: decodedToken.uniqueId,
        instituteName: decodedToken.instituteName,
        isSubscribed: decodedToken.isSubscribed === "true",
      },
      accessToken: token,
      refreshToken,
      expiresAt: decodedToken.exp * 1000,
      refreshTokenExpiresAt: new Date(refreshTokenExpiryTime).getTime(),
      forcePasswordReset,
      forcePinChange,
      emailConfirmed,
    };
  },

  switchProfileToken: async (): Promise<PendingProfileSelection> => {
    const response = await axiosInstance.post<{ data: PendingProfileSelection; succeeded: boolean; messages: string[] }>(
      "auth/SwitchProfileToken"
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.[0] || "Could not open profile switcher");
    }
    return response.data.data;
  },

  setPassword: async (request: {
    challengeToken: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ succeeded: boolean; messages?: string[]; data: unknown }> => {
    const response = await axiosInstance.post('/account/setpassword', request);
    return response.data;
  },

  signup: async (data: SignupRequest): Promise<void> => {
    try {
      const response = await axios.post<SignupResponse>(
        API_URL + "/v1/users",
        data,
        { timeout: 50000 }
      );

      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.[0] || "Registration failed");
      }
    } catch (error) {
      const authError = {
        message:
          error instanceof Error
            ? error.message
            : "An error occurred during signUp",
      };
      throw authError;
    }
  },
};
